import styled from "@emotion/styled";
import { colors, zIndexes } from "@gumtree/ui-library/src/base/theme";
import { Logo } from "@gumtree/ui-library";

const SEARCH_ICON_DATA_URI =
    "data:image/svg+xml,%3Csvg%20width%3D%2222%22%20height%3D%2222%22%20viewBox%3D%220%200%2022%2022%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M10.2223%2018.6667C14.886%2018.6667%2018.6667%2014.886%2018.6667%2010.2222C18.6667%205.55849%2014.886%201.77778%2010.2223%201.77778C5.55854%201.77778%201.77783%205.55849%201.77783%2010.2222C1.77783%2014.886%205.55854%2018.6667%2010.2223%2018.6667Z%22%20stroke%3D%22black%22%20stroke-width%3D%221.33333%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3Cpath%20d%3D%22M19.5556%2019.5556L17.7778%2017.7778%22%20stroke%3D%22black%22%20stroke-width%3D%221.33333%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E";
const MESSAGES_ICON_DATA_URI =
    "data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M17%2020.5H7C4%2020.5%202%2019%202%2015.5V8.5C2%205%204%203.5%207%203.5H17C20%203.5%2022%205%2022%208.5V15.5C22%2019%2020%2020.5%2017%2020.5Z%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-miterlimit%3D%2210%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3Cpath%20d%3D%22M17%209L13.87%2011.5C12.84%2012.32%2011.15%2012.32%2010.12%2011.5L7%209%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-miterlimit%3D%2210%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E";

const MHC_DESKTOP_BG = "#0d475c";
const MHC_DESKTOP_SEARCH_BG = colors.white;
const MHC_DESKTOP_GREEN = "#5ce00b";
const MHC_DESKTOP_BORDER = "#f0f4fa";
const MHC_DESKTOP_NAV_BORDER = "rgba(16, 24, 36, 0.15)";
const MHC_DESKTOP_TEXT_MUTED = "#a0a8b4";
const MHC_DESKTOP_TEXT_DARK = "#3c3241";
const MHC_DESKTOP_INLINE_MEDIA_QUERY = "@media (min-width: 1025px)";

export const NavContainer = styled.nav`
    background-color: ${MHC_DESKTOP_BG};
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0 24px;
    min-height: 80px;
    padding: 0 24px;
    position: relative;
    width: 100%;
    isolation: isolate;
    z-index: ${zIndexes.high};

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        background-color: ${MHC_DESKTOP_BG};
        box-shadow: 0 0 0 100vmax ${MHC_DESKTOP_BG};
        clip-path: inset(0 -100vmax);
        z-index: -1;
    }

    ${MHC_DESKTOP_INLINE_MEDIA_QUERY} {
        flex-wrap: nowrap;
        margin: 0 auto;
        max-width: 1350px;
        padding: 0 8px;

        &::before {
            border-bottom: 1px solid ${MHC_DESKTOP_NAV_BORDER};
        }
    }
`;

export const NavLogo = styled(Logo)`
    display: flex;
    align-items: center;
    flex: 1;
    flex-shrink: 0;
    height: 80px;

    ${MHC_DESKTOP_INLINE_MEDIA_QUERY} {
        flex: 0 0 auto;
    }

    .logo-text {
        display: flex;
    }
`;

export const SearchBarContainer = styled.div`
    background-color: ${MHC_DESKTOP_BG};
    border-bottom: 1px solid ${MHC_DESKTOP_BORDER};
    box-sizing: border-box;
    flex: 1 0 100%;
    margin: 0 -24px;
    min-width: 0;
    order: 3;
    padding: 0 24px 8px;

    ${MHC_DESKTOP_INLINE_MEDIA_QUERY} {
        background-color: transparent;
        border-bottom: 0;
        flex: 1;
        margin: 0;
        order: 0;
        padding: 0;
    }

    .search-bar-container {
        background-color: transparent !important;
        width: 100%;
    }

    .search-bar {
        padding: 0 !important;

        > div {
            display: block !important;
        }

        .inputs-container--fake-search {
            display: none !important;
        }

        .inputs-container--real-search {
            display: flex !important;
            flex-flow: row nowrap !important;
            align-items: center !important;
            padding: 0 0 0 16px !important;
            padding-bottom: 0 !important;
            margin: 0 !important;
            max-width: none !important;
            background-color: ${MHC_DESKTOP_SEARCH_BG} !important;
            border: 0 !important;
            border-radius: 16px !important;
            height: 48px !important;
            box-sizing: border-box !important;
            .keyword-search-container {
                flex: 1;
                min-width: 0;

                .input-field {
                    border-radius: 0 !important;
                    background: transparent !important;
                    height: 48px !important;
                    border: none !important;
                    outline: none !important;
                    box-shadow: none !important;

                    &.is-focused {
                        outline: none !important;
                    }

                    .icon-container {
                        box-sizing: content-box;
                        flex: 0 0 22px;
                        width: 22px;
                        padding-left: 0;
                        padding-right: 16px;

                        .icon {
                            color: transparent;
                            background-image: url("${SEARCH_ICON_DATA_URI}");
                            background-size: 22px 22px;
                            background-repeat: no-repeat;
                            background-position: center;
                            width: 22px;
                            height: 22px;

                            &::before {
                                display: none;
                            }
                        }
                    }

                    .input {
                        font-size: 16px;
                        font-weight: 330;
                        line-height: 24px;
                        color: ${MHC_DESKTOP_TEXT_DARK};

                        &::placeholder {
                            font-size: 16px;
                            font-weight: 330;
                            color: ${MHC_DESKTOP_TEXT_MUTED};
                            opacity: 1;
                        }
                    }

                    .type-ahead-options {
                        border-radius: 16px;

                        .type-ahead-option {
                            border-radius: 16px;
                        }
                    }
                }
            }

            .dividing-line {
                height: 24px !important;
                border-right: 1px solid ${MHC_DESKTOP_BORDER} !important;
                flex-shrink: 0;
            }

            .location-container {
                display: flex !important;
                align-items: center !important;
                flex-grow: 0 !important;
                flex-shrink: 0;
                gap: 8px;
                margin-top: 0 !important;
                max-width: none !important;
                width: auto !important;

                .icon-container {
                    margin-left: 16px;
                    padding-left: 0;
                    padding-right: 0;
                    width: 16px;

                    .icon {
                        width: 16px;
                        height: 16px;
                        font-size: 16px;
                        color: ${MHC_DESKTOP_TEXT_MUTED} !important;
                    }
                }

                .input-field {
                    flex: 0 0 200px;
                    width: 200px;
                    border-radius: 0 !important;
                    background: transparent !important;
                    height: 48px !important;
                    border: none !important;
                    outline: none !important;
                    box-shadow: none !important;

                    &.is-focused {
                        outline: none !important;
                    }

                    .input {
                        padding: 4px 8px 4px 4px !important;
                        font-size: 16px;
                        font-weight: 400;
                        line-height: 24px;
                        color: ${MHC_DESKTOP_TEXT_DARK};

                        &::placeholder {
                            color: ${MHC_DESKTOP_TEXT_MUTED};
                            opacity: 1;
                        }
                    }
                }

                ${MHC_DESKTOP_INLINE_MEDIA_QUERY} {
                    .input-field {
                        flex-basis: auto;
                        width: auto;

                        .input {
                            flex: 0 0 205px;
                            width: 205px;
                        }
                    }
                }

                .search-button {
                    width: auto !important;
                    height: 32px !important;
                    min-width: auto !important;
                    padding: 0 16px !important;
                    border-radius: 10px !important;
                    background-color: ${MHC_DESKTOP_GREEN} !important;
                    border: none !important;
                    margin: 0 8px 0 0 !important;
                    font-size: 12px;
                    font-weight: 500;
                    line-height: 18px;
                    color: #101824;

                    svg {
                        display: none !important;
                    }

                    &::after {
                        content: "Search";
                        font-size: 12px;
                        font-weight: 500;
                        line-height: 18px;
                        color: #101824;
                    }

                    .icon {
                        display: none !important;
                    }
                }
            }
        }
    }
`;

export const RightActions = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 12px;
    height: 80px;
    margin-left: auto;

    && .messages-button,
    && .user-menu > button {
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 48px;
        min-width: 48px;
        height: 48px;
        padding: 0;
        border: 0;
        font-size: 10px;
        font-weight: 330;
        line-height: 18px;
        color: ${colors.white};

        .icon {
            width: 24px;
            height: 24px;
            margin: 0;
            font-size: 24px;
            color: ${colors.white};
        }

        &:hover,
        &:active {
            color: ${colors.white};
        }
    }

    && .user-menu {
        display: inline-block;
        position: relative;
    }

    .messages-button {
        .icon--envelope {
            color: transparent;
            background-image: url("${MESSAGES_ICON_DATA_URI}");
            background-size: 24px 24px;
            background-repeat: no-repeat;
            background-position: center;

            &::before {
                display: none;
            }
        }
    }
`;

export const ActionButton = styled.button`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: none;
    border: none;
    cursor: pointer;
    color: ${colors.white};
    padding: 0;

    .icon {
        width: 24px;
        height: 24px;
        color: ${colors.white};
    }
`;

export const ActionLabel = styled.span`
    font-size: 10px;
    font-weight: 330;
    line-height: 18px;
    color: ${colors.white};
    white-space: nowrap;
`;

export const ActionIconWrapper = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;

    svg {
        width: 24px;
        height: 24px;
    }
`;

export const PostAdButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 24px;
    height: 48px;
    background: none;
    border: 1px solid ${MHC_DESKTOP_BORDER};
    border-radius: 14px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    line-height: 22px;
    color: ${colors.white};
    white-space: nowrap;
    gap: 8px;

    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
`;

export const PostAdLink = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 24px;
    height: 48px;
    background: none;
    border: 1px solid ${MHC_DESKTOP_BORDER};
    border-radius: 14px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: ${colors.white};
    white-space: nowrap;
    text-decoration: none;
    gap: 8px;

    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
        color: ${colors.white};
    }
`;
